import React from "react";
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {NEWWAY_WEB_CLIENT_APP_VERSION} from "./appVersion";

// Set REACT_APP_SENTRY_DSN to an empty string to disable Sentry for a build.
const DEFAULT_SENTRY_DSN = "https://f10e5d22e35ef837272a10030d3b6b7e@o4506581942403072.ingest.us.sentry.io/4512124113190912";
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN === undefined
    ? DEFAULT_SENTRY_DSN
    : process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENVIRONMENT = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";

// Distributed tracing adds `sentry-trace` / `baggage` request headers, which
// the API must allow in its CORS configuration. Keep it opt-in per environment.
const TRACE_PROPAGATION_TARGETS = String(process.env.REACT_APP_SENTRY_TRACE_PROPAGATION_TARGETS || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

// Document Capture result tokens must never leave the browser (see AGENTS.md).
// The token appears both as a query param and as a path segment of the
// session status/result/cancel API calls.
const SENSITIVE_PARAMS = ["result_token", "capture_token"];
const SENSITIVE_PATH_PATTERN = /(\/public\/document-capture\/session\/)[^/?#]+/g;

const parseRate = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
};

const scrubUrl = (url) => {
    if (typeof url !== "string") return url;

    let result = url.replace(SENSITIVE_PATH_PATTERN, "$1[Filtered]");
    if (!result.includes("=")) return result;

    try {
        const parsed = new URL(result, window.location.origin);
        let changed = false;
        SENSITIVE_PARAMS.forEach((param) => {
            if (parsed.searchParams.has(param)) {
                parsed.searchParams.set(param, "[Filtered]");
                changed = true;
            }
        });
        return changed ? parsed.toString() : result;
    } catch (_error) {
        return result;
    }
};

const scrubSpan = (span) => {
    if (!span) return span;
    if (span.description) span.description = scrubUrl(span.description);
    if (span.data?.url) span.data.url = scrubUrl(span.data.url);
    if (span.data?.["http.url"]) span.data["http.url"] = scrubUrl(span.data["http.url"]);
    return span;
};

const scrubBreadcrumb = (breadcrumb) => {
    if (breadcrumb?.data?.url) breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
    if (breadcrumb?.data?.to) breadcrumb.data.to = scrubUrl(breadcrumb.data.to);
    if (breadcrumb?.data?.from) breadcrumb.data.from = scrubUrl(breadcrumb.data.from);
    return breadcrumb;
};

const scrubEvent = (event) => {
    if (event?.request?.url) event.request.url = scrubUrl(event.request.url);
    if (event?.request?.query_string) delete event.request.query_string;
    if (Array.isArray(event?.spans)) event.spans.forEach(scrubSpan);
    if (Array.isArray(event?.breadcrumbs)) event.breadcrumbs.forEach(scrubBreadcrumb);
    return event;
};

export const isSentryEnabled = () => Boolean(SENTRY_DSN);

// Called once from src/instrument.js (the first import of the entry point).
export const initSentry = () => {
    if (!isSentryEnabled()) return;

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: SENTRY_ENVIRONMENT,
        release: `kav-mobility-platform-front@${NEWWAY_WEB_CLIENT_APP_VERSION}`,
        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],
        // Tracing
        tracesSampleRate: parseRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 1.0),
        tracePropagationTargets: ["localhost", ...TRACE_PROPAGATION_TARGETS],
        // Session Replay (text is masked and media blocked by default)
        replaysSessionSampleRate: parseRate(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0.1),
        replaysOnErrorSampleRate: parseRate(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1.0),
        beforeSend: scrubEvent,
        beforeSendTransaction: scrubEvent,
        beforeBreadcrumb: scrubBreadcrumb,
    });
};

export default Sentry;
